import { useMemo, useState } from 'react';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { NHAN_NHOM_NEN_TANG, SO_MODULE_HIEN, TONG_CHUA_CO_QUYEN, TONG_MODULE } from '../constants';
import { useDanhMucQuyen, useDanhSachVaiTro } from '../hooks/useVaiTro';
import { useThaoTacVaiTro } from '../hooks/useThaoTacVaiTro';
import { MA_QUYEN_NEN_TANG, locVaiTro, moduleCuaVaiTro } from '../nghiep-vu/vai-tro';
import type { Role } from '../types';

/**
 * Cấu hình vai trò của tổ chức (cổng tenant, `/he-thong/vai-tro`, G2-D1-4):
 * vai trò lớp 2 — tổ chức tự đặt, gom quyền theo việc người dùng làm. Tạo,
 * đổi tên, phân quyền theo module, xoá. Chỉ quản trị tổ chức vào được (phân hệ
 * `ROLE_CONFIG`). Gán vai trò cho từng người ở màn Cấu hình người dùng.
 *
 * Tải hết danh sách (vài chục vai trò) rồi tìm ở client theo mã hoặc tên.
 */
export function CauHinhVaiTroView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [tuKhoa, setTuKhoa] = useState('');
  const q = useDanhSachVaiTro();
  // Chỉ để đổi mã module thành tên trong cột Quyền; lỗi thì lùi về mã, không chặn bảng.
  const danhMuc = useDanhMucQuyen();
  const thaoTac = useThaoTacVaiTro();
  const tieuDe = t('nav.muc.cauHinhVaiTro');

  // Nhóm quyền nền tảng (ADR-0104) mang tên theo ngôn ngữ giao diện, module mang tên bên nộp gói đặt
  const tenModule = useMemo(
    () =>
      new Map(
        (danhMuc.data ?? []).map((p) => [
          p.module_code,
          p.module_code === MA_QUYEN_NEN_TANG ? t(NHAN_NHOM_NEN_TANG) : p.module_name,
        ]),
      ),
    [danhMuc.data, t],
  );
  const tatCa = q.data ?? [];
  const dong = useMemo(() => locVaiTro(q.data ?? [], tuKhoa), [q.data, tuKhoa]);
  const coLoc = tuKhoa.trim() !== '';
  const dangTai = q.isLoading;

  const columns: TableProps<Role>['columns'] = [
    {
      title: t('vaiTroLop2.cotVaiTro'),
      width: 300,
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-xs flex-col">
          <span className="truncate text-sm font-medium text-content-primary" title={row.name}>
            {row.name}
          </span>
          <span className="truncate font-mono text-2xs text-content-muted" title={row.code}>
            {row.code}
          </span>
        </div>
      ),
    },
    {
      title: t('vaiTroLop2.cotQuyen'),
      width: 320,
      render: (_v, row) => {
        if (row.permissions.length === 0) {
          return <Badge variant={TONG_CHUA_CO_QUYEN}>{t('vaiTroLop2.chuaCoQuyen')}</Badge>;
        }
        const modules = moduleCuaVaiTro(row.permissions);
        const conLai = modules.length - SO_MODULE_HIEN;
        return (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs text-content-secondary">
              {t('vaiTroLop2.soQuyen', { so: soVN(row.permissions.length, locale) })}
            </span>
            <span className="flex flex-wrap gap-1">
              {modules.slice(0, SO_MODULE_HIEN).map((m) => (
                <Badge key={m} variant={TONG_MODULE}>
                  {tenModule.get(m) ?? m}
                </Badge>
              ))}
              {conLai > 0 && (
                <span
                  className="text-2xs text-content-muted"
                  title={modules.slice(SO_MODULE_HIEN).map((m) => tenModule.get(m) ?? m).join(', ')}
                >
                  {t('vaiTroLop2.themModule', { so: conLai })}
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      title: t('vaiTroLop2.cotNguoiDung'),
      dataIndex: 'user_count',
      width: 120,
      align: 'right',
      render: (value: number) =>
        value > 0 ? (
          <span className="text-sm tabular-nums text-content-primary">{soVN(value, locale)}</span>
        ) : (
          <span className="text-xs text-content-muted">{t('vaiTroLop2.chuaAiDung')}</span>
        ),
    },
    {
      title: t('vaiTroLop2.cotTaoLuc'),
      dataIndex: 'created_at',
      width: 120,
      render: (value: string) => (
        <span className="whitespace-nowrap text-xs text-content-muted">{ngayVN(value)}</span>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={row.name}
          muc={[
            {
              khoa: 'phan-quyen',
              nhan: t('vaiTroLop2.phanQuyen'),
              icon: <SafetyOutlined />,
              onChon: () => thaoTac.moPhanQuyen(row),
            },
            {
              khoa: 'doi-ten',
              nhan: t('vaiTroLop2.doiTen'),
              icon: <EditOutlined />,
              onChon: () => thaoTac.moDoiTen(row),
            },
            {
              khoa: 'xoa',
              nhan: t('vaiTroLop2.xoaVaiTro'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              onChon: () => thaoTac.moXoa(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.cauHinhHeThong') },
            { label: t('nav.muc.cauHinhHeThong') },
            { label: tieuDe },
          ]}
          titleIcon={<SafetyCertificateOutlined className="text-primary-600" />}
          title={tieuDe}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={thaoTac.moTao}>
                {t('vaiTroLop2.taoVaiTro')}
              </Button>
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="w-full sm:w-80"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('vaiTroLop2.timPlaceholder')}
            aria-label={t('vaiTroLop2.timNhan')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">
                {t('vaiTroLop2.loiDanhSach', { loi: dichLoi(q.error) })}
              </span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<Role>
                caption={t('vaiTroLop2.bangCaption')}
                rowKey="id"
                dataSource={dong}
                columns={columns}
                loading={dangTai}
                size="middle"
                pagination={false}
                locale={{
                  emptyText: dangTai ? (
                    ' '
                  ) : (
                    <Empty
                      icon={SafetyCertificateOutlined}
                      // Rỗng vì ô tìm: bỏ mô tả và lời mời hành động.
                      title={coLoc ? t('vaiTroLop2.rongLoc', { tuKhoa: tuKhoa.trim() }) : t('vaiTroLop2.rongThat')}
                      description={coLoc ? undefined : t('vaiTroLop2.rongThatMo')}
                      action={
                        coLoc ? undefined : (
                          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={thaoTac.moTao}>
                            {t('vaiTroLop2.taoVaiTroDauTien')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {dong.length > 0 && (
                <div className="border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {coLoc
                      ? t('vaiTroLop2.dangHienLoc', {
                          so: soVN(dong.length, locale),
                          tong: soVN(tatCa.length, locale),
                        })
                      : t('vaiTroLop2.dangHien', { so: soVN(dong.length, locale) })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {thaoTac.hopThoai}
    </div>
  );
}
